import { useQuery } from "@tanstack/react-query";
import { goEntitiesApi } from "@/services/go-entities.service";
import type { GoKlsOption } from "@/types/go-entity";
import { Button } from "@/components/ui/button";

type Props = {
  entityTypeOptions?: GoKlsOption[];
  onOpen: (ginfObjId: number) => void;
};

/** Дерево регионов/районов (плоский список по типу) без требования geometry. */
export function RegionHierarchyHint({ entityTypeOptions = [], onOpen }: Props) {
  const regionType = entityTypeOptions.find((o) =>
    /регион/i.test(o.kls_names || ""),
  );
  const districtType = entityTypeOptions.find((o) =>
    /район|икр/i.test(o.kls_names || ""),
  );

  const regionsQuery = useQuery({
    queryKey: ["go-entities", "regions", regionType?.kls_id],
    queryFn: () =>
      goEntitiesApi.list({
        limit: 200,
        entity_type_kls_id: regionType!.kls_id,
      }),
    enabled: regionType != null,
  });
  const districtsQuery = useQuery({
    queryKey: ["go-entities", "districts", districtType?.kls_id],
    queryFn: () =>
      goEntitiesApi.list({
        limit: 500,
        entity_type_kls_id: districtType!.kls_id,
      }),
    enabled: districtType != null,
  });

  if (!regionType && !districtType) {
    return (
      <div className="text-xs">Типы «регион/район» не найдены в meta — выполните seed entity types.</div>
    );
  }

  const regions = regionsQuery.data?.items ?? [];
  const districts = districtsQuery.data?.items ?? [];

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-foreground">
        Иерархия ({regions.length} рег. / {districts.length} р-нов)
      </div>
      {regionsQuery.isLoading ? (
        <div className="text-xs">Загрузка регионов…</div>
      ) : regions.length === 0 ? (
        <div className="text-xs">Регионы не импортированы — кнопка «Регионы (import)».</div>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
          {regions.map((r) => {
            const kids = districts.filter((d) => d.parent_ginf_obj_id === r.ginf_obj_id);
            return (
              <li key={r.ginf_obj_id} className="rounded border border-border px-2 py-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-foreground">{r.obj_name || `#${r.ginf_obj_id}`}</span>
                  <Button type="button" size="sm" variant="ghost" className="h-6 px-2" onClick={() => onOpen(r.ginf_obj_id)}>
                    Открыть
                  </Button>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  геометрий: {r.map_geometry_count}
                  {r.map_geometry_count === 0 ? " · без контура" : ""}
                </div>
                {kids.length > 0 ? (
                  <ul className="mt-1 space-y-0.5 border-l border-border pl-2">
                    {kids.slice(0, 12).map((d) => (
                      <li key={d.ginf_obj_id} className="flex justify-between gap-1">
                        <span className="truncate">{d.obj_name}</span>
                        <button
                          type="button"
                          className="text-primary underline-offset-2 hover:underline"
                          onClick={() => onOpen(d.ginf_obj_id)}
                        >
                          →
                        </button>
                      </li>
                    ))}
                    {kids.length > 12 ? (
                      <li className="text-muted-foreground">…ещё {kids.length - 12}</li>
                    ) : null}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
